// PhoneLink -- a phone number you can click to call.
//
// Click-to-call through 3CX (2026-10-28): clicking asks Command
// (POST /api/calls/dial) to have 3CX ring YOUR phone / 3CX app first;
// when you pick up, 3CX dials the number. Needs a 3CX extension on your
// Command user (users.phone_extension) and that extension in 3CX API
// client 900's list. If either is missing, or 3CX refuses, it falls
// back to the plain tel: link (OS default dialer / any installed app).
//
// Ctrl/Cmd-click always uses the plain tel: link.
//
// Usage:
//   <PhoneLink number={customer.phone_cell} label="Cell" />
//   <PhoneLink number={lead.phone_main} />                    // no label

import { useState } from 'react';
import axios from 'axios';
import { toast } from '../lib/toast.js';

export default function PhoneLink({ number, label = null, className = '' }) {
  const [busy, setBusy] = useState(false);
  if (!number) return null;
  const digits = String(number).replace(/\D/g, '');
  if (digits.length === 0) return null;
  // Add +1 for 10-digit NANP numbers; leave anything else as-is.
  const e164 = digits.length === 10
    ? `+1${digits}`
    : (digits.startsWith('1') && digits.length === 11 ? `+${digits}` : `+${digits}`);
  const display = String(number); // preserve operator-entered formatting

  async function onClick(e) {
    if (e.ctrlKey || e.metaKey || digits.length < 7) return; // plain tel:
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const r = await axios.post('/api/calls/dial', { number: digits });
      toast.success('Calling ' + display, `Your phone (ext ${r.data.ext}) is ringing. Pick up to connect.`);
    } catch (err) {
      const data = err.response && err.response.data;
      if (data && data.code === 'no_extension') {
        window.location.href = `tel:${e164}`;
      } else {
        toast.error('Could not start the call', (data && data.error) || err.message);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <span className={`phone-link ${className}`.trim()} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      {label && <span style={{ color: 'var(--color-text-dim)', marginRight: 4 }}>{label}:</span>}
      <a
        href={`tel:${e164}`}
        onClick={onClick}
        className="cell-mono"
        style={{ color: 'inherit', textDecoration: 'none', borderBottom: '1px dotted var(--color-text-faint)', opacity: busy ? 0.6 : 1 }}
        title="Click to call through 3CX (your phone rings first). Ctrl-click for your default dialer."
      >
        {display}
      </a>
    </span>
  );
}
